import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  CalendarIcon,
  Phone,
  MapPin,
  Clock,
  Loader2,
  CheckCircle2,
  MessageCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { CLINIC, SERVICES } from "@/lib/clinic";
import { buildWhatsAppLink } from "@/components/site/WhatsApp";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "09:00", "10:00", "11:00", "12:00", "13:00",
  "15:00", "16:00", "17:00", "18:00",
];

const SERVICE_OPTIONS = [
  ...SERVICES.map((s) => s.name === "Braces / Orthodontics" ? "Braces / Orthodontics" : s.name),
  "Other",
];

export default function BookingContact({ waNumber }) {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", service: "", message: "",
  });
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: typeof e === "string" ? e : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service || !date || !time) {
      toast.error("Please fill your name, phone, service, date and time.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        service: form.service,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time,
        message: form.message.trim() || null,
      };
      await axios.post(`${API}/appointments`, payload);
      setSubmitted(true);
      toast.success("Appointment requested! We'll call you shortly to confirm.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
      setDate(undefined);
      setTime("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Something went wrong. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="section-contact" className="py-20 md:py-28 bg-[#FAFAF9]">
      <div className="container-x">
        <div className="max-w-2xl">
          <span className="inline-block text-xs uppercase tracking-[0.2em] text-[#2D4A3E] font-medium">
            Book & visit us
          </span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C221F] tracking-tight">
            Request an appointment.
          </h2>
          <p className="mt-4 text-[#57605B]">
            Pick a date and time that works for you — our team will call to confirm within a few hours.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-6 lg:gap-10">
          {/* Form */}
          <div className="lg:col-span-7 bg-white rounded-3xl border border-stone-200 p-6 sm:p-8 soft-shadow">
            {submitted ? (
              <div data-testid="booking-success" className="text-center py-10">
                <div className="mx-auto w-14 h-14 rounded-full bg-[#2D4A3E] text-white flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7" strokeWidth={1.75} />
                </div>
                <h3 className="mt-5 font-display text-2xl font-semibold text-[#1C221F]">
                  Appointment requested!
                </h3>
                <p className="mt-2 text-[#57605B] max-w-md mx-auto">
                  Thank you — our team will call you shortly to confirm. For urgent care, please
                  call us at {CLINIC.phoneDisplay}.
                </p>
                <Button
                  onClick={() => setSubmitted(false)}
                  variant="outline"
                  className="mt-6 rounded-full border-[#2D4A3E]/25 text-[#2D4A3E]"
                  data-testid="btn-book-another"
                >
                  Book another
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-4" data-testid="booking-form">
                <div className="sm:col-span-1">
                  <Label htmlFor="name" className="text-sm">Full name *</Label>
                  <Input
                    id="name"
                    data-testid="input-name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className="mt-1.5 rounded-xl h-11 bg-[#FAFAF9]"
                    required
                  />
                </div>
                <div className="sm:col-span-1">
                  <Label htmlFor="phone" className="text-sm">Phone number *</Label>
                  <Input
                    id="phone"
                    data-testid="input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="e.g. 99969 27080"
                    className="mt-1.5 rounded-xl h-11 bg-[#FAFAF9]"
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="email" className="text-sm">Email <span className="text-[#57605B]">(optional)</span></Label>
                  <Input
                    id="email"
                    data-testid="input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className="mt-1.5 rounded-xl h-11 bg-[#FAFAF9]"
                  />
                </div>

                <div className="sm:col-span-2">
                  <Label className="text-sm">Service *</Label>
                  <Select value={form.service} onValueChange={update("service")}>
                    <SelectTrigger data-testid="select-service" className="mt-1.5 rounded-xl h-11 bg-[#FAFAF9]">
                      <SelectValue placeholder="What can we help with?" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`option-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-1">
                  <Label className="text-sm">Preferred date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        data-testid="btn-pick-date"
                        className={cn(
                          "mt-1.5 w-full rounded-xl h-11 bg-[#FAFAF9] justify-start font-normal text-left",
                          !date && "text-[#57605B]"
                        )}
                      >
                        <CalendarIcon className="w-4 h-4 mr-2" strokeWidth={1.75} />
                        {date ? format(date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="sm:col-span-1">
                  <Label className="text-sm">Preferred time *</Label>
                  <Select value={time} onValueChange={setTime}>
                    <SelectTrigger data-testid="select-time" className="mt-1.5 rounded-xl h-11 bg-[#FAFAF9]">
                      <SelectValue placeholder="Pick a time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`option-time-${t}`}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="sm:col-span-2">
                  <Label htmlFor="message" className="text-sm">Message <span className="text-[#57605B]">(optional)</span></Label>
                  <Textarea
                    id="message"
                    data-testid="input-message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us anything that helps us prepare — pain level, prior treatments, etc."
                    className="mt-1.5 rounded-xl bg-[#FAFAF9] min-h-[110px]"
                  />
                </div>

                <div className="sm:col-span-2 flex flex-wrap items-center gap-3 mt-2">
                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="btn-submit-booking"
                    className="rounded-full bg-[#2D4A3E] hover:bg-[#21362D] text-white h-12 px-8"
                  >
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Requesting…</>
                    ) : (
                      "Request appointment"
                    )}
                  </Button>
                  <Button
                    asChild
                    type="button"
                    variant="outline"
                    className="rounded-full h-12 px-6 border-[#25D366]/50 text-[#1FBA5A] hover:bg-[#25D366]/10"
                  >
                    <a
                      href={buildWhatsAppLink(waNumber || CLINIC.phoneDial)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="btn-whatsapp-form"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" strokeWidth={2} />
                      Book on WhatsApp
                    </a>
                  </Button>
                  <a
                    href={`tel:${CLINIC.phoneDial}`}
                    data-testid="btn-call-form"
                    className="inline-flex items-center gap-2 text-sm text-[#2D4A3E] font-medium hover:underline underline-offset-4"
                  >
                    <Phone className="w-4 h-4" strokeWidth={1.75} />
                    Or call us at {CLINIC.phoneDisplay}
                  </a>
                </div>
              </form>
            )}
          </div>

          {/* Contact info */}
          <aside className="lg:col-span-5 flex flex-col gap-5">
            <div className="bg-[#2D4A3E] text-white rounded-3xl p-6 sm:p-8 soft-shadow-lg">
              <h3 className="font-display text-2xl font-semibold">Visit City Smiles</h3>
              <p className="mt-2 text-white/70 text-sm">We&apos;re open 6 days a week. Walk-ins welcome.</p>

              <ul className="mt-6 space-y-4 text-sm">
                <li className="flex gap-3">
                  <MapPin className="w-5 h-5 shrink-0 mt-0.5 text-[#D4B27E]" strokeWidth={1.75} />
                  <div data-testid="clinic-address">{CLINIC.address}</div>
                </li>
                <li className="flex gap-3">
                  <Phone className="w-5 h-5 shrink-0 mt-0.5 text-[#D4B27E]" strokeWidth={1.75} />
                  <a href={`tel:${CLINIC.phoneDial}`} data-testid="clinic-phone" className="hover:underline">
                    {CLINIC.phoneDisplay}
                  </a>
                </li>
                <li className="flex gap-3">
                  <Clock className="w-5 h-5 shrink-0 mt-0.5 text-[#D4B27E]" strokeWidth={1.75} />
                  <div data-testid="clinic-hours">{CLINIC.hoursLine}</div>
                </li>
              </ul>

              <a
                href={CLINIC.googleMapsLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="btn-directions"
                className="inline-flex items-center gap-2 mt-6 bg-[#D4B27E] text-[#1C221F] rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#C2A16F] transition-colors"
              >
                Get directions →
              </a>
              <a
                href={buildWhatsAppLink(waNumber || CLINIC.phoneDial)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="btn-whatsapp-sidebar"
                className="inline-flex items-center gap-2 mt-3 ml-3 bg-[#25D366] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#1FBA5A] transition-colors"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={2} />
                WhatsApp
              </a>
            </div>

            <div className="rounded-3xl overflow-hidden border border-stone-200 soft-shadow">
              <iframe
                title="City Smiles Dental Clinic location on Google Maps"
                src={CLINIC.googleMapsEmbed}
                className="w-full h-[300px] border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
